/**
 * Element factories. The zod schemas keep `type` as a required discriminator (so a union parse can
 * never be ambiguous), which makes `PhotoElement.parse({...})` verbose everywhere. These helpers add
 * the discriminator and an id, apply all schema defaults, and are the ONLY place elements are born.
 */
import { nanoid } from 'nanoid';
import {
  PhotoElement,
  ShapeElement,
  StickerElement,
  TextElement,
  type Frame,
  type PhotoElement as PhotoElementT,
  type ShapeElement as ShapeElementT,
  type StickerElement as StickerElementT,
  type TextElement as TextElementT,
} from './schema';

const nid = (): string => nanoid(12);

/** Everything a caller may pass except the discriminator (which we add) — id is optional. */
type Init<T> = Omit<{ [K in keyof T]?: unknown }, 'type'> & { id?: string };

/** `rotationDeg` has a schema default, so callers may omit it. */
export type FrameInit = Omit<Frame, 'rotationDeg'> & { rotationDeg?: number };

export function makePhotoElement(init: Init<PhotoElementT> & { frame: FrameInit }): PhotoElementT {
  return PhotoElement.parse({ id: nid(), ...init, type: 'photo' });
}

export function makeTextElement(init: Init<TextElementT> & { frame: FrameInit }): TextElementT {
  return TextElement.parse({ id: nid(), content: { paragraphs: [''] }, ...init, type: 'text' });
}

export function makeStickerElement(init: Init<StickerElementT> & { frame: FrameInit; assetId: string }): StickerElementT {
  return StickerElement.parse({ id: nid(), ...init, type: 'sticker' });
}

export function makeShapeElement(init: Init<ShapeElementT> & { frame: FrameInit }): ShapeElementT {
  return ShapeElement.parse({ id: nid(), shape: 'rect', ...init, type: 'shape' });
}

/** Frame helper: mm box with no rotation. */
export function frameMm(xMm: number, yMm: number, wMm: number, hMm: number, rotationDeg = 0): Frame {
  return { xMm, yMm, wMm, hMm, rotationDeg };
}
